/**
 * The perk registry.
 *
 * One import and one entry per perk, and nothing else. `PERK_ORDER` is not decoration: it is the
 * order `perkLoadout` aggregates in, so two peers holding the same perks sum in the same sequence
 * and land on the same last bit.
 *
 * The order is by branch and then by tier, matching `PERK_BRANCH_ORDER`, so the list reads as the
 * tree does. That is convenience for a reader; the guarantee is only that it is fixed.
 */

import { ADRENAL_RESERVE } from '@/sim/progression/perks/survival/adrenalReserve';
import { BIN_PICKER } from '@/sim/progression/perks/scavenging/binPicker';
import { BRASS_DISCIPLINE } from '@/sim/progression/perks/gunplay/brassDiscipline';
import { BUDDY_SYSTEM } from '@/sim/progression/perks/survival/buddySystem';
import { CALM_UNDER_FIRE } from '@/sim/progression/perks/gunplay/calmUnderFire';
import { CIVIL_DEFENCE } from '@/sim/progression/perks/survival/civilDefence';
import { CONNOISSEUR } from '@/sim/progression/perks/scavenging/connoisseur';
import { HAULIER } from '@/sim/progression/perks/scavenging/haulier';
import { IRON_CONSTITUTION } from '@/sim/progression/perks/survival/ironConstitution';
import { LAST_STAND } from '@/sim/progression/perks/survival/lastStand';
import { LIGHT_FINGERS } from '@/sim/progression/perks/scavenging/lightFingers';
import { LOOTERS_INSTINCT } from '@/sim/progression/perks/scavenging/lootersInstinct';
import { MUSCLE_MEMORY } from '@/sim/progression/perks/gunplay/muscleMemory';
import { PACK_MULE } from '@/sim/progression/perks/scavenging/packMule';
import { QUICK_FINGERS } from '@/sim/progression/perks/scavenging/quickFingers';
import { RUNNING_RELOAD } from '@/sim/progression/perks/gunplay/runningReload';
import { SECOND_WIND } from '@/sim/progression/perks/gunplay/secondWind';
import { SENSIBLE_SHOES } from '@/sim/progression/perks/survival/sensibleShoes';
import { SNAP_SIGHT } from '@/sim/progression/perks/gunplay/snapSight';
import { STEADY_HANDS } from '@/sim/progression/perks/gunplay/steadyHands';
import { STILL_KICKING } from '@/sim/progression/perks/survival/stillKicking';
import { STRONG_BACK } from '@/sim/progression/perks/scavenging/strongBack';
import { SUNDAY_GUNNER } from '@/sim/progression/perks/gunplay/sundayGunner';
import { SURGICAL_INTENT } from '@/sim/progression/perks/gunplay/surgicalIntent';
import { TOURNIQUET_DRILL } from '@/sim/progression/perks/survival/tourniquetDrill';
import type { Perk, PerkId } from '@/sim/progression/perkContract';
import type { PerkBranchId } from '@/sim/progression/perkBranchContract';

export const PERKS = {
  steadyHands: STEADY_HANDS,
  sundayGunner: SUNDAY_GUNNER,
  brassDiscipline: BRASS_DISCIPLINE,
  runningReload: RUNNING_RELOAD,
  snapSight: SNAP_SIGHT,
  calmUnderFire: CALM_UNDER_FIRE,
  surgicalIntent: SURGICAL_INTENT,
  secondWind: SECOND_WIND,
  muscleMemory: MUSCLE_MEMORY,
  civilDefence: CIVIL_DEFENCE,
  ironConstitution: IRON_CONSTITUTION,
  sensibleShoes: SENSIBLE_SHOES,
  tourniquetDrill: TOURNIQUET_DRILL,
  adrenalReserve: ADRENAL_RESERVE,
  buddySystem: BUDDY_SYSTEM,
  lastStand: LAST_STAND,
  stillKicking: STILL_KICKING,
  packMule: PACK_MULE,
  quickFingers: QUICK_FINGERS,
  strongBack: STRONG_BACK,
  binPicker: BIN_PICKER,
  lootersInstinct: LOOTERS_INSTINCT,
  haulier: HAULIER,
  connoisseur: CONNOISSEUR,
  lightFingers: LIGHT_FINGERS,
} as const satisfies Record<PerkId, Perk>;

/** Aggregation order, and part of the determinism contract. */
export const PERK_ORDER: readonly PerkId[] = [
  'steadyHands',
  'sundayGunner',
  'brassDiscipline',
  'runningReload',
  'snapSight',
  'calmUnderFire',
  'surgicalIntent',
  'secondWind',
  'muscleMemory',
  'civilDefence',
  'ironConstitution',
  'sensibleShoes',
  'tourniquetDrill',
  'adrenalReserve',
  'buddySystem',
  'lastStand',
  'stillKicking',
  'packMule',
  'quickFingers',
  'strongBack',
  'binPicker',
  'lootersInstinct',
  'haulier',
  'connoisseur',
  'lightFingers',
];

/** Perk lookup, total over the union so it never widens to `undefined`. */
export function perk(id: PerkId): Perk {
  return PERKS[id];
}

/** Whether `value` names a perk. Used when reading perk ranks back out of a save. */
export function isPerkId(value: string): value is PerkId {
  return Object.hasOwn(PERKS, value);
}

/** One column of the tree, in tier then registry order. What the upgrade screen draws. */
export function perksInBranch(branch: PerkBranchId): readonly Perk[] {
  return PERK_ORDER.map(perk)
    .filter((entry) => entry.branch === branch)
    .sort((a, b) => a.tier - b.tier);
}
